<script setup lang="ts">
import type { OrganizationJs } from "@/generated/models";
import FormattedDate from "ls/components/FormattedDate.vue";
import { DialogTitle } from "reka-ui";
import { computed } from "vue";
import { FreemiumUsageStatus, useOrganizationCloudPlusStatus } from "../useOrganizationCloudPlusStatus";
import CloudPlusLimitIndicator from "./CloudPlusLimitIndicator.vue";

const props = defineProps<{
  organization: OrganizationJs;
  isOnBillingPage?: boolean;
}>();

const {
  documentsCount,
  whiteboardsCount,
  templatesCount,
  librariesCount,
  documentsLimit,
  whiteboardsLimit,
  templatesLimit,
  librariesLimit,
  status,
  whitelistExpiration,
  isPending,
  isWhitelisted,
} = useOrganizationCloudPlusStatus(() => props.organization);

const title = computed(() => {
  if (status.value === FreemiumUsageStatus.overLimit) {
    return "You're over your free plan limits";
  }

  if (status.value === FreemiumUsageStatus.atLimit) {
    return "You've hit your limit";
  }

  return `${props.organization.Name} is on the free plan`;
});

const currentWhiteboardsCount = computed(() => {
  return isPending.value ? 0 : whiteboardsCount.value;
});

const currentDocumentsCount = computed(() => {
  return isPending.value ? 0 : documentsCount.value;
});

const templatesPercentage = computed(() => {
  return templatesCount.value / templatesLimit.value * 100;
});

const librariesPercentage = computed(() => {
  return librariesCount.value / librariesLimit.value * 100;
});

function statusColor(percentage: number) {
  if (percentage > 100) return "text-red-600";
  if (percentage >= 66) return "text-orange-400";
  return "";
}
</script>

<template>
  <div class="flex flex-col gap-2 rounded-sm py-4 text-slate-500">
    <div class="text-lg/6 font-semibold text-slate-800">
      <DialogTitle v-if="!isOnBillingPage">
        {{ title }}
      </DialogTitle>
      <div v-else>
        {{ title }}
      </div>
    </div>
    <div v-if="isWhitelisted">
      Thank you for being a part of the whiteboards and documents beta. You can continue using them for free until <FormattedDate :value="whitelistExpiration!" short />. After that, you can upgrade to Cloud Plus or delete items to get under the free limit.
    </div>
    <div v-else>
      <div v-if="status === FreemiumUsageStatus.overLimit">
        You've exceeded the free limit for whiteboards and documents. Upgrade to Cloud Plus for unlimited whiteboards and documents or delete items to get under the limit.
      </div>
      <div v-if="status === FreemiumUsageStatus.atLimit">
        You've reached the free limit for whiteboards and documents. Upgrade to Cloud Plus for unlimited whiteboards and documents.
      </div>
      <div v-if="status === FreemiumUsageStatus.free || status === FreemiumUsageStatus.nearLimit">
        The free plan includes 3 whiteboards and 10 documents. Upgrade to Cloud Plus for unlimited whiteboards and documents.
      </div>
    </div>
    <div class="mt-4 flex flex-col gap-6">
      <CloudPlusLimitIndicator type="Whiteboards" :current="currentWhiteboardsCount" :limit="whiteboardsLimit" />
      <CloudPlusLimitIndicator type="Documents" :current="currentDocumentsCount" :limit="documentsLimit" />
      <div>
        <p class="text-sm font-medium text-slate-800">
          Shared assets
        </p>
        <div class="mt-1 flex flex-row gap-4 text-sm text-slate-800">
          <span>
            Templates <span :class="statusColor(templatesPercentage)">{{ templatesCount }}/{{ templatesLimit }}</span>
          </span>
          <span>
            Libraries <span :class="statusColor(librariesPercentage)">{{ librariesCount }}/{{ librariesLimit }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
